import { Button, ClickEvent, Label, Snackbar, Tab, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { AutogenLayoutBreakthroughFuelList } from "./autogen/AutogenLayoutBreakthroughFuelList";
import { OrderTabView } from "@mcleod/dispatch";
import { BreakthroughFuelHold } from "./BreakthroughFuelHold";
import { BreakthroughFuelReady } from "./BreakthroughFuelReady";
import { ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

const ORDER_FIELD = "order_id";

export class BreakthroughFuelList extends AutogenLayoutBreakthroughFuelList {
    onLoad(): void | Promise<void> {
        this.searchChildren();
        const holdLayout = this.layoutHold as BreakthroughFuelHold;
        const readyLayout = this.layoutReady as BreakthroughFuelReady;
        holdLayout.tableBreakthrough.addRowDisplayListener(this.commonOnRowDisplay.bind(this));
        readyLayout.tableBreakthrough.addRowDisplayListener(this.commonOnRowDisplay.bind(this));
        // custom row edit resolution to make sure changing hold status reflects in both tables
        holdLayout.tableBreakthrough.doOnResolveRowEdit = this.commonDoOnResolveRowEdit(true).bind(this);
        readyLayout.tableBreakthrough.doOnResolveRowEdit = this.commonDoOnResolveRowEdit(false).bind(this);

        holdLayout.refreshTabsCallback = () => this.refreshTabs();
        readyLayout.refreshTabsCallback = () => this.refreshTabs();

        readyLayout.buttonHold.addClickListener((event: ClickEvent) =>
            this.holdActionOnClick(event, readyLayout.tableBreakthrough.selectedRows, true));

        holdLayout.buttonRemoveHold.addClickListener((event: ClickEvent) =>
            this.holdActionOnClick(event, holdLayout.tableBreakthrough.selectedRows, false));
    }

    refreshTabs() {
        this.searchChildren();
    }

    searchChildren() {
        this.layoutHold.mainDataSource.search().then(this.getTabTitleCallback(this.tabHold, "Hold"));
        this.layoutReady.mainDataSource.search().then(this.getTabTitleCallback(this.tabReady, "Ready to Process"));
    }

    getTabTitleCallback(tab: Tab, baseCaption: string) {
        return response => {
            tab.caption = `${baseCaption} (${response.modelRows?.length})`;
        }
    }

    commonOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const orderLabel = tableRow.findComponentById("labelOrderId")
        const orderId = tableRow.data.get(ORDER_FIELD);
        if (orderId) {
            this.setupOrderLink(tableRow.findComponentsByField(ORDER_FIELD)[0] as Label, tableRow);
        } else {
            orderLabel.visible = false;
            orderLabel.enabled = false;
        }
    }

    commonDoOnResolveRowEdit(wasOnHold: boolean) {
        return async (updatedRow: ModelRow) => {
            const isNowOnHold = updatedRow.get("on_hold") === "Y";
            if (wasOnHold !== isNowOnHold) {
                //need to refresh data if the record will be in the other table
                setTimeout(() => this.searchChildren(), 0);
                return null;
            }
            return updatedRow;
        }
    }

    setupOrderLink(comp: Label, tableRow: TableRow) {
        const orderId = tableRow.data.get(ORDER_FIELD);
        if (orderId) {
            comp.applyLinkStyle();
            comp.addClickListener(() => {
                OrderTabView.navigateTo({ key: orderId, newTab: true });
            });
        }
    }

    holdActionOnClick(event: ClickEvent, selectedRows: TableRow[], placeHold: boolean) {
        const button = event.target as Button;
        if (selectedRows.length === 0) {
            Snackbar.showSnackbar("Please select at least one row to update.");
            return;
        }
        const holdMessage = placeHold ? "Place selected transactions on hold?" : "Remove hold from selected transactions?";
        CommonDialogs.showYesNo(holdMessage, "Breakthrough Fuel Hold").then(async res => {
            if (!!res) {
                let successCount = 0;
                button.busy = true;
                try {
                    await Promise.all(selectedRows.map(async row => {
                        try {
                            row.data.set("on_hold", placeHold);
                            await row.data.post();
                            successCount++;
                        } catch (error) {
                            row.data.revertToOriginalData("on_hold");
                            throw error;
                        }
                    }));
                    Snackbar.showSnackbar(StringUtil.pluralString(successCount, "Breakthrough Fuel Hold", "Breakthrough Fuel Holds") + " updated successfully.");
                } finally {
                    button.busy = false;
                    this.refreshTabs();
                }
            }
        });
    }
}
